import type { SalesEntityType } from '../../domain/entities/sales.entity';
import { useTransactionEvents } from '../hooks/use-transaction-events';
import { useEntityEvents } from '../hooks/use-entity-events';
import { EventRow } from '../shared/transaction-event-row';
import { ErrorState, SectionCard } from '../shared/display';
import { usePageQueryParam } from '@/shared/hooks/use-page-query-param';

const PER_PAGE = 5;

const THIS_ENTITY: Record<SalesEntityType, string> = {
  Transaction: 'esta transação',
  Contract: 'este contrato',
  BillingAgreement: 'esta billing agreement',
  DebtArrangement: 'esta renegociação',
  Invoice: 'esta invoice',
  SaleWebhookInbox: 'este webhook',
};

/**
 * Aba Auditoria da página de uma entidade do Sales. A transação tem rota
 * própria de eventos; as outras usam a genérica. Os dois hooks são chamados e
 * só um dispara, pela regra dos hooks.
 */
export function EntityEventsSection({
  entityType,
  entityId,
}: {
  entityType: SalesEntityType;
  entityId: string;
}) {
  // Param dedicado: `page` é das outras listas da página.
  const [page, setPage] = usePageQueryParam('events_page');
  const isTransaction = entityType === 'Transaction';
  const transactionEvents = useTransactionEvents(
    entityId,
    page,
    PER_PAGE,
    isTransaction,
  );
  const entityEvents = useEntityEvents(
    entityType,
    entityId,
    page,
    PER_PAGE,
    !isTransaction,
  );
  const { data, isLoading, isFetching, isError, refetch } = isTransaction
    ? transactionEvents
    : entityEvents;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;

  return (
    <SectionCard title="Histórico de Eventos">
      <div className="space-y-4">
        <p className="text-(--color-text-secondary) text-sm">
          Os eventos que tornaram {THIS_ENTITY[entityType]} o que é hoje, dos
          mais recentes para os mais antigos.
        </p>

        {isLoading ? (
          <ul className="space-y-3">
            {Array.from({ length: PER_PAGE }).map((_, i) => (
              <li
                key={i}
                className="border-(--color-border) bg-(--color-bg-secondary) h-20 animate-pulse rounded-lg border"
              />
            ))}
          </ul>
        ) : isError ? (
          <ErrorState
            message="Não foi possível carregar os eventos."
            onRetry={() => void refetch()}
          />
        ) : !data || data.items.length === 0 ? (
          <p className="text-(--color-text-secondary) py-8 text-center text-sm">
            Nenhum evento registrado para {THIS_ENTITY[entityType]}.
          </p>
        ) : (
          <ol>
            {data.items.map((event, i) => (
              <EventRow
                key={event.id}
                event={event}
                isLast={i === data.items.length - 1}
                showWebhookOrigin
              />
            ))}
          </ol>
        )}

        <div className="flex items-center justify-between">
          <span className="text-(--color-text-secondary) text-xs">
            {data ? (
              <>
                {data.total} {data.total === 1 ? 'evento' : 'eventos'} · página{' '}
                {page} de {totalPages}
              </>
            ) : null}
          </span>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isFetching}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-(--color-text-secondary) text-xs">
                {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isFetching}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
}
